import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Users, ArrowLeft, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same canonical labels used in MemberDirectory + admin
const CIEES_ROLE_LABELS = {
  presidente: "Presidente",
  vice_presidente: "Vice-Presidente",
  presidente_assembleia: "Presidente da Assembleia",
  presidente_conselho_fiscal: "Presidente do Conselho Fiscal",
  direcao_executiva: "Direção Executiva",
  vogal: "Vogal da Direção",
  comunidades: "Comunidades",
  financeiro: "Financeiro",
  tesoureiro: "Tesoureiro(a)",
  formacao: "Educação",
  espiritualidade: "Espiritualidade",
  comunicacao: "Comunicação",
  secretario: "Secretária(o) da Direção",
  assessor_espiritual: "Assessor Espiritual",
};
// Canonical display order
const ROLE_ORDER = [
  "presidente", "vice_presidente", "presidente_assembleia",
  "presidente_conselho_fiscal", "direcao_executiva", "vogal",
  "assessor_espiritual",
  "comunidades", "financeiro", "tesoureiro", "formacao",
  "espiritualidade", "comunicacao", "secretario",
];

export default function CommunityDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    axios.get(`${API}/communities/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <section className="container-page py-20"><p className="text-[hsl(var(--muted-foreground))]">A carregar…</p></section>;
  if (error || !data) return (
    <section className="container-page py-20" data-testid="community-not-found">
      <div className="overline">Comunidade</div>
      <h1 className="display-heading text-4xl mt-3">Comunidade não encontrada.</h1>
      <p className="mt-6 text-sm text-[hsl(var(--muted-foreground))]">
        A comunidade que procura pode ter mudado de endereço ou já não estar ativa.
      </p>
      <Link to="/comunidades" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
        <ArrowLeft className="h-3.5 w-3.5" /> Voltar à lista
      </Link>
    </section>
  );

  // Group team members by role (canonical order)
  const teamByRole = {};
  for (const member of (data.team || [])) {
    const k = member.ciees_role;
    if (!teamByRole[k]) teamByRole[k] = [];
    teamByRole[k].push(member);
  }
  const orderedRoles = ROLE_ORDER.filter((r) => teamByRole[r]);

  return (
    <section data-testid="community-detail-page">
      {/* Hero */}
      <div className="container-page pt-10 lg:pt-14">
        <Link to="/comunidades" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">
          <ArrowLeft className="h-3.5 w-3.5" /> Comunidades
        </Link>
        <div className="mt-6 flex items-center gap-2 overline" style={{ color: data.accent_color }}>
          <MapPin className="h-3.5 w-3.5" /> {data.region || "Comunidade CIEES"}
        </div>
        <h1 className="display-heading text-5xl sm:text-6xl lg:text-7xl mt-4">{data.name}.</h1>
        <span className="accent-rule mt-6 block" style={{ background: data.accent_color }} />

        {data.description_pt && (
          <p className="mt-7 max-w-3xl text-lg text-[hsl(var(--foreground))] leading-relaxed">
            {data.description_pt}
          </p>
        )}

        <div className="mt-8 flex flex-wrap gap-3 text-[11px] uppercase tracking-[0.18em]">
          {data.city && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[hsl(var(--border))]">
              <MapPin className="h-3 w-3" /> {data.city}
            </span>
          )}
          {data.founded_year && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[hsl(var(--border))]">
              <Calendar className="h-3 w-3" /> Desde {data.founded_year}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[hsl(var(--border))]" data-testid="community-member-count">
            <Users className="h-3 w-3" /> {data.member_count} {data.member_count === 1 ? "sócio" : "sócios"}
          </span>
        </div>
      </div>

      {/* Team */}
      <div className="container-page mt-16 lg:mt-24 pb-16 lg:pb-24">
        <div className="overline">Equipa local</div>
        <h2 className="display-heading text-3xl sm:text-4xl mt-3">A comunidade tem rosto.</h2>
        <span className="accent-rule mt-5 block" />

        {orderedRoles.length === 0 ? (
          <div className="mt-10 bg-white border border-[hsl(var(--border))] p-8" data-testid="community-team-empty">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              A equipa local desta comunidade está a ser constituída.
            </p>
          </div>
        ) : (
          <div className="mt-10 space-y-10" data-testid="community-team">
            {orderedRoles.map((roleKey) => (
              <div key={roleKey}>
                <div className="overline text-[hsl(var(--muted-foreground))]">{CIEES_ROLE_LABELS[roleKey] || roleKey}</div>
                <ul className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {teamByRole[roleKey].map((m) => (
                    <li key={m.id} className="flex items-center gap-4 p-4 bg-white border border-[hsl(var(--border))]" data-testid={`community-team-member-${m.id}`}>
                      {m.picture ? (
                        <img src={m.picture} alt={m.name} className="h-14 w-14 rounded-full object-cover" />
                      ) : (
                        <div className="h-14 w-14 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-base font-medium text-[hsl(var(--primary))]">
                          {(m.name || "?").slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{m.name}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
